import { JwtHelperService } from '@auth0/angular-jwt';
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { User } from '../../models/user';
import { Token } from '../../models/token';
import _ from 'lodash';
import { Observable, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { LoggedUserData } from '../../models/loggedUserData';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AccessTokenResponse } from '../../models/accessTokenResponse';

@Injectable()
export class AuthControllerService {
  static showRegisteredMessage = false;
  baseUrl: string = environment.authUrl;
  OAuthRedirectUrl: string = environment.OAuthRedirectUrl;
  OAuthClientId: string = environment.OAuthClientId;
  OAuthSecret: string = environment.OAuthSecret;
  jwtHelperService: JwtHelperService = new JwtHelperService();
  tokenStorage: any = environment.strictLogout ? sessionStorage : localStorage;
  private subscribers: (() => void)[] = [];

  savedToken() {
    const token = this.tokenStorage.getItem(Token.tokeNameInSotage);

    //I have no idea how that is possible
    if (token === 'null') {
      this.router.navigate(['/login']);
    }
    return token;
  }

  savedRefreshToken() {
    return this.tokenStorage.getItem(Token.refreshTokenNameInStorage);
  }

  constructor(
    private http: HttpClient,
    private router: Router,
    public translate: TranslateService
  ) {}

  register(user: User) {
    return this.http.put(this.baseUrl + 'new_user', user, {
      headers: new HttpHeaders({
        Authorization:
          'Basic ' + btoa(this.OAuthClientId + ':' + this.OAuthSecret),
      }),
    });
  }

  login(authenticationCode: string): Observable<AccessTokenResponse> {
    return this.http.post<AccessTokenResponse>(
      this.baseUrl + 'access_token',
      this.jsonToQueryString({
        grant_type: 'authorization_code',
        code: authenticationCode,
        redirect_uri: this.OAuthRedirectUrl,
      }),
      {
        headers: new HttpHeaders({
          'Content-Type': 'application/x-www-form-urlencoded',
          Authorization:
            'Basic ' + btoa(this.OAuthClientId + ':' + this.OAuthSecret),
        }),
      }
    );
  }

  refreshToken(): Observable<AccessTokenResponse> {
    return this.http.post<AccessTokenResponse>(
      this.baseUrl + 'access_token',
      this.jsonToQueryString({
        grant_type: 'refresh_token',
        refresh_token: this.savedRefreshToken(),
        redirect_uri: this.OAuthRedirectUrl,
      }),
      {
        headers: new HttpHeaders({
          'Content-Type': 'application/x-www-form-urlencoded',
          Authorization:
            'Basic ' + btoa(this.OAuthClientId + ':' + this.OAuthSecret),
        }),
      }
    );
  }

  subscribeForTokenChange(subscriber: () => void) {
    this.subscribers.push(subscriber);
  }

  unsubscribe(subscriber: () => void) {
    this.subscribers = this.subscribers.filter((sub) => {
      return sub !== subscriber;
    });
  }

  notifySubscribers(): void {
    this.subscribers.forEach((it) => it());
  }

  isLogged() {
    return this.isSavedTokenValid();
  }

  loggedUser() {
    if (!this.isLogged()) return;
    return this.savedUser();
  }

  isUserEmailVerified() {
    if (!this.isLogged()) return;
    return this.loggedUser().email_verified;
  }

  isUserVerified() {
    if (!this.isLogged()) return;
    return this.loggedUser().user_verified;
  }

  requestNewPassword(username: string) {
    if (!username) {
      throw new Error('Please fill login name !');
    }
    return this.http.post(this.baseUrl + '/user/password/new', {
      username: username,
    });
  }

  logout() {
    let authHeader = this.getAuthHeader();
    authHeader['responseType'] = 'text';
    return this.http
      .get(this.baseUrl + 'userdata/invalidateToken', authHeader)
      .pipe(
        catchError((e) => {
          console.log(e.message);
          return of(e);
        }),
        finalize(() => {
          this.removeTokens();
          return this.router.navigate(['/login']);
        })
      )
  }

  pingForValidToken() {
    const authHeader = this.getAuthHeader();
    return this.http.post(this.baseUrl + 'userdata/userTokenValid', authHeader);
  }

  private getAuthHeader() {
    return {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.savedToken(),
      }),
    };
  }

  randomId() {
    let text = '';
    let possible =
      'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    for (let i = 0; i < 15; i++)
      text += possible.charAt(Math.floor(Math.random() * possible.length));
    return text;
  }

  isSavedTokenValid() {
    const token = this.savedToken();
    return token && this.isValid(token);
  }

  isAnyTokenPresent() {
    return !!this.savedToken();
  }

  getSearchParameters(): any {
    const prmstr = window.location.search.substr(1);
    return prmstr != null && prmstr != ''
      ? this.transformToAssocArray(prmstr)
      : {};
  }

  savedTokenHasExpired() {
    return this.jwtHelperService.isTokenExpired(this.savedToken());
  }

  savedTokenIsAboutToExpireIn5Minutes() {
    return this.jwtHelperService.isTokenExpired(this.savedToken(), 60 * 5);
  }

  savedTokenIsExpiredFor20Hours() {
    return this.jwtHelperService.isTokenExpired(
      this.savedToken(),
      -(60 * 60 * 20)
    );
  }

  confirmMail(): Observable<any> {
    return this.http.post(
      this.baseUrl + 'email/verify?lang=' + this.translate.currentLang,
      {},
      Object.assign(
        { observe: 'response', responseType: 'text' },
        this.getAuthHeader()
      ) as any
    );
  }

  saveAuthToken(token): void {
    this.tokenStorage.setItem(Token.tokeNameInSotage, token);
    this.notifySubscribers();
  }

  private transformToAssocArray(prmstr): object {
    let params = {};
    let prmarr = prmstr.split('&');
    for (let i = 0; i < prmarr.length; i++) {
      let tmparr = prmarr[i].split('=');
      params[tmparr[0]] = tmparr[1];
    }
    return params;
  }

  private requestAuthToken(authCode: string): Observable<any> {
    return this.http.post(
      this.baseUrl + 'access_token',
      this.jsonToQueryString({
        grant_type: 'authorization_code',
        code: authCode,
        redirect_uri: this.OAuthRedirectUrl,
      }),
      {
        headers: new HttpHeaders({
          'Content-Type': 'application/x-www-form-urlencoded',
          Authorization:
            'Basic ' + btoa(this.OAuthClientId + ':' + this.OAuthSecret),
        }),
      }
    );
  }

  private jsonToQueryString(json: any) {
    return Object.keys(json)
      .map(function (key) {
        return encodeURIComponent(key) + '=' + encodeURIComponent(json[key]);
      })
      .join('&');
  }

  public removeTokens() {
    this.tokenStorage.removeItem(Token.tokeNameInSotage);
    this.tokenStorage.removeItem(Token.refreshTokenNameInStorage);
  }

  saveRefreshToken(token) {
    this.tokenStorage.setItem(Token.refreshTokenNameInStorage, token);
  }

  isValid(token): boolean {
    try {
      const user = this.parseToken(token);
      return token !== undefined && user !== undefined;
    } catch (e) {
      return false;
    }
  }

  private parseToken(token) {
    return this.jwtHelperService.decodeToken(token);
  }

  private savedUser() {
    return this.parseToken(this.savedToken());
  }
}
